import { useMemo, useEffect } from "react";
import Joyride from "react-joyride";
import { SearchInput } from "./components/SearchInput";
import { useTrackSearchUsed } from "@/hooks/useTrackSearchUsed";
import { PatientTable, TabType } from "./components/PatientTable";
import { PatientDetailModal } from "./components/PatientDetailModal";
import { PatientsJoyrideTooltip } from "./components/PatientsJoyrideTooltip";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose,
} from "@/components/ui/dialog";
import { X, Info } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { usePatientsData } from "./hooks/usePatientsData";
import { usePatientsTour } from "./hooks/usePatientsTour";
import { buildTourPatients } from "./utils/buildTourPatients";
import { NoAgendamientoModal } from "@/components/psychologist/patients/no-agendamiento/NoAgendamientoModal";
import { usePsychologistContext } from "@/contexts/PsychologistContext";
import { useRankingTransition } from "@/hooks/useRankingTransition";
import { isNZContactOrigin } from "@/utils/contactChannel";
import { useWidth } from "@/hooks/useWidth";
import { useIsPwaApp } from "@/pwa/PwaModeContext";

interface PatientsContentProps {
  onSchedule?: (processId: string) => void;
}

export function PatientsContent({ onSchedule }: PatientsContentProps) {
  const { size } = useWidth();
  const isMobile = size === 's';
  // App mobile: sin onboarding guiado (solo web) ni exportar CSV (una descarga no tiene sentido en la app).
  const isPwaApp = useIsPwaApp();

  const {
    activeTab,
    setActiveTab,
    query,
    setQuery,
    page,
    setPage,
    loading,
    visiblePatients,
    tabs,
    typeFilter,
    setTypeFilter,
    pagoFilter,
    setPagoFilter,
    modalOpen,
    setModalOpen,
    modalReason,
    modalPatientName,
    detailPatient,
    setDetailPatient,
    detailPatientInitialTab,
    detailPatientAutoOpenNote,
    onDetailTabChange,
    onViewPatient,
    onViewPatientDerivation,
    onReadConsultation,
    onOpenNoSchedule,
    onOpenRefer,
    onScheduleFromList,
    onGoToCalendar,
    onAddNote,
    onViewNotes,
    onMarkUrgent,
    onUnmarkUrgent,
    noAgendamientoModalParams,
    closeNoAgendamientoModal,
  } = usePatientsData({ onSchedule });

  // Métricas de Actividad: cuenta el USO del buscador (nunca lo escrito).
  useTrackSearchUsed("search_patients_list", query, visiblePatients.length);

  const { runTour, tourKey, tourStepIndex, steps, canSkip, onTourCallback, onReplayTour } =
    usePatientsTour({ patientsLoaded: !loading && !isPwaApp, isOnAllTab: activeTab === "all" && !isPwaApp });
  const tourPatients = useMemo(
    () => buildTourPatients(tourStepIndex, activeTab, visiblePatients),
    [tourStepIndex, activeTab, visiblePatients],
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const PatientsTooltipAny = PatientsJoyrideTooltip as any;

  const { services, loadServices } = usePsychologistContext();
  const { rankingActual, rankingAnterior } = useRankingTransition();
  // NZ, u O que viene de NZ: no puede iniciar derivaciones hasta validar con Ops primero.
  const disableReferForm = isNZContactOrigin(rankingActual, rankingAnterior);

  useEffect(() => { loadServices(); }, [loadServices]);

  // Derive available therapy types from the psychologist's own services
  const therapyTypeOptions = useMemo(() => {
    const names = services.map((s) => s.service.name);
    return names
      .filter((name) => !name.toLowerCase().includes("taller") && !name.toLowerCase().includes("supervisión") && !name.toLowerCase().includes("supervision"))
      .map((name) => ({ value: name.toLowerCase(), label: name }))
      .filter((opt, idx, arr) => arr.findIndex((o) => o.value === opt.value) === idx);
  }, [services]);

  const statusLabel = (status?: string) => {
    if (status === "lead") return "Nuevo";
    if (status === "active") return "Activo";
    if (status === "on_hold") return "En pausa";
    if (status === "inactive") return "Inactivo";
    if (status === "transferred") return "Derivación exitosa";
    return "";
  };

  const handleExportCSV = () => {
    if (visiblePatients.length === 0) return;
    const inactiveHeaders = ["Nombre", "Email", "Tipo de proceso", "Última sesión", "Total sesiones"];
    const headersByTab: Record<TabType, string[]> = {
      all: ["Nombre", "Email", "Estado", "Estado de pago", "Tipo de proceso", "Próxima sesión", "Total sesiones", "Última sesión"],
      active: ["Nombre", "Email", "Nacionalidad", "Tipo de proceso", "Primera sesión", "Última sesión", "Próxima sesión", "Total sesiones", "Estado de pago"],
      lead: ["Nombre", "Email", "Nacionalidad", "Estado de pago", "Tipo de proceso", "Próxima sesión", "Motivo de consulta"],
      inactive: inactiveHeaders,
      on_hold: inactiveHeaders,
      transferred: inactiveHeaders,
    };
    const headers = headersByTab[activeTab];
    const rows = visiblePatients.map((p) => {
      const email = p.totalSessions === 0 ? "" : (p.email ?? "");
      if (activeTab === "all")
        return [p.name, email, statusLabel(p.patientStatus), p.paymentStatus ?? "", p.service ?? "", p.nextSession ?? "", p.totalSessions, p.totalSessions > 0 ? (p.lastSession ?? "") : ""];
      if (activeTab === "active")
        return [p.name, email, p.nationality ?? "", p.service ?? "", p.firstSession ?? "", p.totalSessions > 0 ? (p.lastSession ?? "") : "", p.nextSession ?? "", p.totalSessions, p.paymentStatus ?? ""];
      if (activeTab === "lead")
        return [p.name, email, p.nationality ?? "", p.paymentStatus ?? "", p.service ?? "", p.nextSession ?? "", p.consultationReason ?? ""];
      return [p.name, email, p.service ?? "", p.totalSessions > 0 ? (p.lastSession ?? "") : "", p.totalSessions];
    });
    const csv = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pacientes-${activeTab}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Show Pago filter only for tabs where payment status is meaningful
  const showPagoFilter = activeTab === "lead" || activeTab === "all" || activeTab === "active";

  return (
    <section style={{ padding: isMobile ? "1rem 1rem 5rem" : "1.25rem 2rem 5rem" }}>
      {!isPwaApp && (
      <Joyride
        key={tourKey}
        steps={steps}
        run={runTour}
        stepIndex={tourStepIndex}
        continuous
        showSkipButton={canSkip}
        scrollToFirstStep
        disableOverlayClose
        disableCloseOnEsc
        spotlightClicks={false}
        disableScrolling={false}
        hideCloseButton
        styles={{ options: { primaryColor: '#6D57BC', zIndex: 2000 } }}
        locale={{ back: 'Atrás', close: 'Cerrar', last: 'Entendido', next: 'Continuar', skip: 'Saltar' }}
        tooltipComponent={PatientsTooltipAny}
        callback={onTourCallback}
      />
      )}

      {/* Repasar guía — sutil, siempre visible (mobile y desktop), salvo en la app mobile */}
      {!isPwaApp && (
      <div className="mb-2 flex justify-end">
        <button
          type="button"
          onClick={onReplayTour}
          title="Repasar guía de Pacientes"
          aria-label="replay-patients-onboarding"
          className="flex-shrink-0 inline-flex items-center gap-1 text-[0.75rem] font-normal text-[#8475b3] hover:text-[#6D57BC] bg-transparent border-0 cursor-pointer transition-colors"
        >
          <Info size={14} />
          Repasar guía de Pacientes
        </button>
      </div>
      )}

      {/* Header row — hidden on mobile */}
      {!isMobile && !isPwaApp && (
        <div className="mb-5 flex items-center justify-end">
          <button
            type="button"
            onClick={handleExportCSV}
            disabled={loading || visiblePatients.length === 0}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.375rem",
              border: "2px solid #6D57BC",
              borderRadius: "9999px",
              padding: "0.5rem 0.875rem",
              background: "transparent",
              color: "#6D57BC",
              fontWeight: 500,
              fontSize: "0.8125rem",
              height: "35px",
              cursor: "pointer",
              transition: "all 0.2s ease-out",
              opacity: (loading || visiblePatients.length === 0) ? 0.4 : 1,
            }}
          >
            Exportar
          </button>
        </div>
      )}

      {/* Primary tab row — en mobile es horizontalmente scrolleable (más tabs que ancho
          disponible); el fade del borde derecho avisa que hay más tabs fuera de la vista. */}
      <div className={isMobile ? "relative mb-4" : "mb-4"}>
        <div
          id="patients-tour-tabs"
          className="flex items-center gap-2"
          style={isMobile ? { overflowX: "auto", flexWrap: "nowrap", paddingBottom: "4px", scrollbarWidth: "none" } : { flexWrap: "wrap" }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => { setActiveTab(tab.id as TabType); setPage(1); }}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-primary text-white border-primary shadow-sm"
                  : "bg-white border-gray-200 text-gray-600 hover:border-primary/50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {isMobile && (
          <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent" />
        )}
      </div>

      {/* Search + filter toolbar */}
      <div className={`mb-4 flex gap-2 ${isMobile ? "flex-col" : "flex-row items-center"}`}>
        <div id="patients-tour-search" className={isMobile ? "w-full" : "flex-1"}>
          <SearchInput value={query} onChange={setQuery} placeholder="Buscar por nombre o email..." />
        </div>

        {(therapyTypeOptions.length >= 2 || showPagoFilter) && (
          <div className={`flex gap-2 items-center ${isMobile ? "w-full" : ""}`}>
            {/* Tipo filter */}
            {therapyTypeOptions.length >= 2 && (
              <Select
                value={typeFilter}
                onValueChange={(v) => { setTypeFilter(v); setPage(1); }}
              >
                <SelectTrigger className={`h-11 shrink-0 border border-border rounded-lg px-3 text-sm hover:border-primary/50 focus:ring-1 focus:ring-primary bg-card ${isMobile ? "flex-1" : "w-[140px]"}`}>
                  <span className="text-muted-foreground mr-1.5 text-xs font-medium">Tipo</span>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {therapyTypeOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Pago filter */}
            {showPagoFilter && (
              <Select
                value={pagoFilter}
                onValueChange={(v) => { setPagoFilter(v); setPage(1); }}
              >
                <SelectTrigger className={`h-11 shrink-0 border border-border rounded-lg px-3 text-sm hover:border-primary/50 focus:ring-1 focus:ring-primary bg-card ${isMobile ? "flex-1" : "w-[140px]"}`}>
                  <span className="text-muted-foreground mr-1.5 text-xs font-medium">Pago</span>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="paid">Pagado</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        )}
      </div>

      <PatientTable
        activeTab={activeTab}
        currentPage={page}
        onPageChange={setPage}
        patients={tourPatients}
        loading={loading}
        onViewPatient={onViewPatient}
        onViewPatientDerivation={onViewPatientDerivation}
        onReadConsultation={(reason, p) => onReadConsultation(reason, p)}
        onOpenNoSchedule={onOpenNoSchedule}
        onOpenRefer={onOpenRefer}
        disableReferForm={disableReferForm}
        onScheduleFromList={onScheduleFromList}
        onGoToCalendar={onGoToCalendar}
        onAddNote={onAddNote}
        onViewNotes={onViewNotes}
        onMarkUrgent={onMarkUrgent}
        onUnmarkUrgent={onUnmarkUrgent}
      />

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md w-full p-0 overflow-hidden">
          <div className="px-6 pt-6 pb-4 relative" style={{ background: 'linear-gradient(180deg, #f5f3ff 0%, #ffffff 100%)' }}>
            <DialogClose className="absolute top-4 right-4 rounded-full w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
              <X size={16} />
            </DialogClose>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold pr-8" style={{ color: '#1e1b4b' }}>
                Motivo de consulta
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500 mt-1">{modalPatientName}</p>
          </div>

          <div className="px-6 py-4">
            {modalReason ? (
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap m-0">{modalReason}</p>
            ) : (
              <p className="text-sm text-gray-500 m-0">Sin motivo de consulta disponible.</p>
            )}
          </div>

          <div className="px-6 pb-6 pt-1 flex justify-end">
            <Button onClick={() => setModalOpen(false)}>Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {detailPatient && (
        <PatientDetailModal patient={detailPatient} onClose={() => setDetailPatient(null)} initialTab={detailPatientInitialTab} autoOpenNote={detailPatientAutoOpenNote} onTabChange={onDetailTabChange} />
      )}

      <NoAgendamientoModal params={noAgendamientoModalParams} onClose={closeNoAgendamientoModal} />
    </section>
  );
}
